import React, { useEffect, useMemo, useState } from 'react'
import classnames from 'classnames'
import Iconfont from '@/components/iconfont/iconfont'
import { Column, Row } from '@/components/layout/layout'
import useSelectList from '@/utils/hooks/useSelectList'
import { layerManage } from '@/utils/layer'
import { IMemberGroupListItem, IMemberLevelDetail } from '@/model/types/member'
import { WithInputLimit } from '@/utils'
import FilterContainer from '../../goods-manage/components/filter-container/filter-container'

export interface MemberFilterValue {
    groupIds: number[]
    memberLevels: number[]
    memberPay: number[]
    memberTypes: number[]
    otherFilter: number[]
    balanceMin?: number
    balanceMax?: number
    pointsMin?: number
    pointsMax?: number
}

interface Props {
    value: MemberFilterValue
    groups: IMemberGroupListItem[]
    levels: IMemberLevelDetail[]
    onConfirm: (value: MemberFilterValue) => void
}

interface MemberFilterOption {
    id: number
    name: string
}

interface SelectionHandle {
    has: (val: MemberFilterOption) => boolean
    toggle: (val: MemberFilterOption) => void
}

export const getDefaultMemberFilterValue = (): MemberFilterValue => ({
    groupIds: [],
    memberLevels: [],
    memberPay: [],
    memberTypes: [],
    otherFilter: [],
    balanceMin: undefined,
    balanceMax: undefined,
    pointsMin: undefined,
    pointsMax: undefined,
})

const moneyLimitRegExp = /^\d{0,8}(\.\d{0,2})?$/
const integerLimitRegExp = /^\d{0,9}$/
const memberPayOptions = [
    { id: 0, name: '正常' },
    { id: 1, name: '过期' },
    { id: 2, name: '未支付' },
]
const memberTypeOptions = [
    { id: 0, name: '小程序会员' },
    { id: 1, name: '手工会员' },
    { id: 2, name: '老零售会员' },
    { id: 3, name: '表格导入会员' },
]
const otherFilterOptions = [
    { id: 0, name: '收银台消费过' },
    { id: 1, name: '商城消费过' },
    { id: 2, name: '充值过' },
    { id: 3, name: '有会员余额' },
]

const pickOptions = (options: MemberFilterOption[], ids: number[]) => options.filter(option => ids.includes(option.id))
const numberToString = (value?: number) => (value === undefined ? '' : `${value}`)
const stringToNumber = (value: string) => (value === '' ? undefined : Number(value))
const renderLimitInput = (placeholder: string, value: string, onChange: (value: string) => void, reg: RegExp) =>
    WithInputLimit(
        <input
            className="member-list-filter__ipt"
            placeholder={placeholder}
            value={value}
            onChange={e => onChange(e.currentTarget.value)}
        />,
        reg,
    )

const renderTagRow = (label: string, options: MemberFilterOption[], selection: SelectionHandle) => (
    <Row className="member-list-filter__row" data-align-items-center>
        <div className="member-list-filter__label">{label}</div>
        <Row className="member-list-filter__options">
            {options.length ? (
                options.map(item => {
                    const selected = selection.has(item)

                    return (
                        <div
                            className={classnames('member-list-filter__tag', {
                                'member-list-filter__tag--active': selected,
                            })}
                            key={item.id}
                            onClick={() => selection.toggle(item)}
                        >
                            <span>{item.name}</span>
                            {selected ? (
                                <div className="member-list-filter__tag-icon">
                                    <Iconfont name="iconyixuan" size={14} />
                                </div>
                            ) : null}
                        </div>
                    )
                })
            ) : (
                <span className="member-list-filter__empty">暂无数据</span>
            )}
        </Row>
    </Row>
)

const MemberFilter: React.FC<Props> = props => {
    const groupOptions = useMemo(() => props.groups.map(group => ({ id: group.id, name: group.name })), [props.groups])
    const levelOptions = useMemo(() => props.levels.map(level => ({ id: level.level, name: level.name })), [
        props.levels,
    ])
    const groupSelection = useSelectList<MemberFilterOption>(pickOptions(groupOptions, props.value.groupIds), 'id')
    const levelSelection = useSelectList<MemberFilterOption>(pickOptions(levelOptions, props.value.memberLevels), 'id')
    const paySelection = useSelectList<MemberFilterOption>(pickOptions(memberPayOptions, props.value.memberPay), 'id')
    const typeSelection = useSelectList<MemberFilterOption>(
        pickOptions(memberTypeOptions, props.value.memberTypes),
        'id',
    )
    const otherSelection = useSelectList<MemberFilterOption>(
        pickOptions(otherFilterOptions, props.value.otherFilter),
        'id',
    )
    const [balanceMin, setBalanceMin] = useState(numberToString(props.value.balanceMin))
    const [balanceMax, setBalanceMax] = useState(numberToString(props.value.balanceMax))
    const [pointsMin, setPointsMin] = useState(numberToString(props.value.pointsMin))
    const [pointsMax, setPointsMax] = useState(numberToString(props.value.pointsMax))

    useEffect(() => {
        groupSelection.setSelect(pickOptions(groupOptions, props.value.groupIds))
        levelSelection.setSelect(pickOptions(levelOptions, props.value.memberLevels))
        paySelection.setSelect(pickOptions(memberPayOptions, props.value.memberPay))
        typeSelection.setSelect(pickOptions(memberTypeOptions, props.value.memberTypes))
        otherSelection.setSelect(pickOptions(otherFilterOptions, props.value.otherFilter))
        setBalanceMin(numberToString(props.value.balanceMin))
        setBalanceMax(numberToString(props.value.balanceMax))
        setPointsMin(numberToString(props.value.pointsMin))
        setPointsMax(numberToString(props.value.pointsMax))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [groupOptions, levelOptions, props.value])

    const reset = () => {
        groupSelection.setSelect([])
        levelSelection.setSelect([])
        paySelection.setSelect([])
        typeSelection.setSelect([])
        otherSelection.setSelect([])
        setBalanceMin('')
        setBalanceMax('')
        setPointsMin('')
        setPointsMax('')
    }

    return (
        <FilterContainer
            className="member-list-filter__container"
            onCancel={() => layerManage.pop()}
            onReset={reset}
            onConfirm={() => {
                props.onConfirm({
                    groupIds: groupSelection.select.map(item => item.id),
                    memberLevels: levelSelection.select.map(item => item.id),
                    memberPay: paySelection.select.map(item => item.id),
                    memberTypes: typeSelection.select.map(item => item.id),
                    otherFilter: otherSelection.select.map(item => item.id),
                    balanceMin: stringToNumber(balanceMin),
                    balanceMax: stringToNumber(balanceMax),
                    pointsMin: stringToNumber(pointsMin),
                    pointsMax: stringToNumber(pointsMax),
                })
                layerManage.pop()
            }}
        >
            <Column className="member-list-filter">
                {renderTagRow('会员分组', groupOptions, groupSelection)}
                {renderTagRow('会员等级', levelOptions, levelSelection)}
                {renderTagRow('付费会员', memberPayOptions, paySelection)}
                {renderTagRow('会员类型', memberTypeOptions, typeSelection)}
                {renderTagRow('其他筛选', otherFilterOptions, otherSelection)}
                <Row className="member-list-filter__row" data-align-items-center>
                    <div className="member-list-filter__label">会员余额</div>
                    <Row className="member-list-filter__range">
                        {renderLimitInput('最低余额', balanceMin, setBalanceMin, moneyLimitRegExp)}
                        <div className="member-list-filter__line" />
                        {renderLimitInput('最高余额', balanceMax, setBalanceMax, moneyLimitRegExp)}
                    </Row>
                </Row>
                <Row className="member-list-filter__row" data-align-items-center>
                    <div className="member-list-filter__label">会员积分</div>
                    <Row className="member-list-filter__range">
                        {renderLimitInput('最低积分', pointsMin, setPointsMin, integerLimitRegExp)}
                        <div className="member-list-filter__line" />
                        {renderLimitInput('最高积分', pointsMax, setPointsMax, integerLimitRegExp)}
                    </Row>
                </Row>
            </Column>
        </FilterContainer>
    )
}

export default MemberFilter
